import {
  CalcProps,
  CreateTooltipParams,
  RectangleProps,
  SvgProps
} from '@anm/components/charts/ChartBar';
import {
  ChartData,
  D3MouseEvent,
  SvgParam
} from '@anm/components/charts/types';
import MONTHS from '@anm/constants/months';
import getDateMonthYear from '@anm/helpers/date/getDateMonthYear';
import { select } from 'd3-selection';

import { CHART_BAR_MARGIN_RIGHT } from '.';

const getChartLabel = (dateMs?: number) => {
  const [date, month] = getDateMonthYear(dateMs);
  return `${MONTHS[month]?.slice(0, 3)} ${date}`;
};

const createDivWithClass = (el: HTMLDivElement, className: string) =>
  select(el)
    .append('div')
    .attr('class', `${className}`);

export const createTooltips = ({
  data,
  rects,
  chartRef,
  rectWidth
}: CreateTooltipParams) => (getTooltipLabel: (value: number) => string) => {
  const tooltipTop = createDivWithClass(
    chartRef?.current!,
    'tooltip top'
  ).style('transform', `translate(calc(-50% + ${rectWidth / 2}px), -120%)`);

  const tooltipBottom = createDivWithClass(
    chartRef?.current!,
    'tooltip bottom'
  ).style('transform', `translate(calc(-50% + ${rectWidth / 2}px), 20%)`);

  chartRef?.current?.querySelectorAll('.label')?.forEach(el => el.remove());

  const xStartLabel = createDivWithClass(
    chartRef?.current!,
    'horizontal-chart-label label'
  )
    .style('left', `0`)
    .text(getChartLabel(+data[0].label));

  const xEndLabel = createDivWithClass(
    chartRef?.current!,
    'horizontal-chart-label label'
  )
    .style('right', `${CHART_BAR_MARGIN_RIGHT}px`)
    .text(getChartLabel(+data[data.length - 1].label));

  const handleBarMouseOver = (
    { target }: D3MouseEvent,
    { label, value }: ChartData
  ) => {
    const { x, y } = target;
    const baseVal = 'baseVal';
    const topPosition = y[baseVal].valueAsString;
    const leftPosition = x[baseVal].valueAsString;

    const tooltipLabel = getTooltipLabel(value);
    const [date, month] = getDateMonthYear(+label);

    const tooltipHoverText = `${MONTHS[month].slice(0, 3)} ${date} `;

    xStartLabel.style('opacity', '0');
    xEndLabel.style('opacity', '0');

    tooltipTop
      .text(tooltipLabel)
      .style('opacity', '1')
      .style('top', `${topPosition}px`)
      .style('left', `${leftPosition}px`);

    tooltipBottom
      .text(tooltipHoverText)
      .style('opacity', '1')
      .style('color', '#919EAA')
      .style('bottom', '-23px')
      .style('left', `${leftPosition}px`);
  };

  const handleBarMouseOut = () => {
    xStartLabel.style('opacity', '1');
    xEndLabel.style('opacity', '1');
    tooltipTop.style('opacity', '0');
    tooltipBottom.style('opacity', '0');
  };

  rects
    .on('mouseover', handleBarMouseOver as any)
    .on('mouseout', handleBarMouseOut);
};

export const createLabels = ({
  data,
  width,
  height,
  margin,
  chartRef,
  maxValue,
  barMargin,
  rectHeight,
  chartWidthRatio,
  barsVerticalPadding
}: RectangleProps & SvgProps & SvgParam & CalcProps) => {
  const axisBottomOffset = 4;

  const maxAllowedWidth = (width - margin.right) * chartWidthRatio;
  const widthRatio = maxAllowedWidth / maxValue;

  const reversedData = [...data].reverse();

  const calcBottom = ({ value }: ChartData) => {
    const index = reversedData.findIndex(
      (data: ChartData) => data.value === value
    );
    const gap = index ? barMargin : 0;

    const bottom =
      height -
      gap +
      rectHeight -
      barsVerticalPadding -
      margin.bottom +
      axisBottomOffset -
      (index + 1) * rectHeight;

    return `${bottom}px`;
  };

  const calcMaxWidth = ({ value }: ChartData) => {
    const maxWidth = width - value * widthRatio - margin.right;
    return `${maxWidth}px`;
  };

  const labels = chartRef?.current?.querySelectorAll('.label');
  labels?.forEach(el => el?.remove());

  select(chartRef?.current)
    .selectAll('div')
    .data(data)
    .enter()
    .append('div')
    .attr('class', 'vertical-chart-label')
    .style('bottom', calcBottom)
    .style('max-width', calcMaxWidth)
    .style('left', ({ value }: ChartData) => `${value * widthRatio}px`)
    .html(({ value, label }: ChartData) => `<b>${value}</b> ${label}`);
};
